import React, { useEffect, useState } from 'react';
import { Check, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOrganization, useUser } from '@clerk/react';
import * as Sentry from '@sentry/react';
import { UserProfile } from '../types';
import { api } from '../services/api';
import { useClerkOrgSync } from '../hooks/useClerkOrgSync';
import {
  companyFromClerkMeta,
  resolveProfileFormDefaults,
} from '../src/profile/prefillFromClerk';

interface Props {
  user: UserProfile;
  onBack: () => void;
  onLogout: () => void;
  onUpdate: (updated: Partial<UserProfile>) => void;
}

export const ProfilePage: React.FC<Props> = ({ user, onUpdate }) => {
  const { user: clerkUser } = useUser();
  const { organization } = useOrganization();
  const { syncOrg } = useClerkOrgSync();

  // Primitive Clerk sources — keep effect deps stable (metadata objects churn).
  const clerkFirstName = clerkUser?.firstName ?? '';
  const clerkLastName = clerkUser?.lastName ?? '';
  const clerkCompanyName = companyFromClerkMeta(
    clerkUser?.unsafeMetadata as Record<string, unknown> | undefined,
    clerkUser?.publicMetadata as Record<string, unknown> | undefined,
    organization?.name,
  );
  const orgName = organization?.name ?? '';

  const defaults = resolveProfileFormDefaults({
    appFirstName: user.firstName,
    appLastName: user.lastName,
    appCompanyName: user.companyName,
    clerkFirstName,
    clerkLastName,
    clerkUnsafeMetadata: clerkCompanyName
      ? { companyName: clerkCompanyName }
      : undefined,
    organizationName: orgName,
  });

  // DUTCH-14: seed from app profile, fill gaps from Clerk identity/metadata.
  const [firstName, setFirstName] = useState(defaults.firstName);
  const [lastName, setLastName] = useState(defaults.lastName);
  const [companyName, setCompanyName] = useState(defaults.companyName);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // When Clerk hydrates after mount (or org name arrives), fill only still-empty
  // fields so we never clobber in-progress edits or saved app profile values.
  useEffect(() => {
    const next = resolveProfileFormDefaults({
      appFirstName: user.firstName,
      appLastName: user.lastName,
      appCompanyName: user.companyName,
      clerkFirstName,
      clerkLastName,
      clerkUnsafeMetadata: clerkCompanyName
        ? { companyName: clerkCompanyName }
        : undefined,
      organizationName: orgName,
    });
    setFirstName((prev) => (prev.trim() ? prev : next.firstName));
    setLastName((prev) => (prev.trim() ? prev : next.lastName));
    setCompanyName((prev) => (prev.trim() ? prev : next.companyName));
  }, [
    user.firstName,
    user.lastName,
    user.companyName,
    clerkFirstName,
    clerkLastName,
    clerkCompanyName,
    orgName,
  ]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const data = await api.patch<{ firstName: string; lastName: string; companyName: string }>(
        '/api/users/profile',
        { firstName: firstName.trim(), lastName: lastName.trim(), companyName: companyName.trim() },
      );
      onUpdate({ firstName: data.firstName, lastName: data.lastName, companyName: data.companyName });

      // Idempotent Clerk org sync — transient failures are swallowed inside syncOrg.
      await syncOrg({ name: companyName });

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: unknown) {
      Sentry.captureException(err, { tags: { source: 'profile-save' } });
      setSaveError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
      <div className="min-h-screen bg-bg">
      <main className="max-w-6xl mx-auto w-full px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Account</p>
          <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">Profiel</h1>
          <p className="text-sm text-ink/55 mt-2">Beheer je naam en kantoorgegevens.</p>
        </div>

        {/* D-08: Identity & security delegated to Clerk's embedded portal */}
        <Link
          to="/account/profile"
          className="profile-link flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent/80 transition-colors mb-6"
        >
          <ExternalLink size={14} />
          Identiteit, e-mail en wachtwoord beheren
        </Link>

        <section className="bg-white rounded-2xl border border-black/8 p-6 md:p-7 mb-6 max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/55 mb-5">Kantoorgegevens</p>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Voornaam</label>
                <input value={firstName} onChange={e => setFirstName(e.target.value)}
                  className="w-full border border-black/10 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Achternaam</label>
                <input value={lastName} onChange={e => setLastName(e.target.value)}
                  className="w-full border border-black/10 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Bedrijfsnaam</label>
              <input value={companyName} onChange={e => setCompanyName(e.target.value)}
                className="w-full border border-black/10 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all" />
            </div>
            <div className="flex items-center gap-8 pt-1">
              <div>
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Rol</label>
                <span className={`inline-block px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] rounded-md ${
                  user.role === 'beheerder' ? 'bg-accent/15 text-accent' : 'bg-ink/5 text-ink/60'
                }`}>{user.role}</span>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Credits</label>
                <span className="text-lg font-bold text-ink">{user.credits}</span>
              </div>
            </div>
            {saveError && (
              <p className="text-sm text-red-600 font-medium">{saveError}</p>
            )}
            <div className="pt-2">
              <button onClick={handleSave} disabled={saving}
                className="bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : null}
                {saved ? 'Opgeslagen' : 'Opslaan'}
              </button>
            </div>
          </div>
        </section>

      </main>
      </div>
  );
};
